<template>
    <div class="details-container" @click.stop="$emit('close')">
        <div class="d-flex flex-column" style="background: #FFFFFF; height: 95vh; width: 780px; margin: auto; overflow: hidden; border-radius: .5em" @click.stop="">
            <header class="flex-shrink-0 py-3">
                <div
                    class="col-12 mb-2 pb-2"
                    style="border-bottom: #d9d9d9 1px solid"
                >
                    <div class="d-flex justify-content-end">
                        <input
                            type="checkbox"
                            class="form-check-input"
                            v-model="showAllPhotos"
                            id="showAllPhotosCheckbox"
                        >
                        <span class="ms-2 me-4">{{ $t('observations.show_all_photos') }}</span>
                        <button class="btn btn-sm btn-secondary mx-1" @click.stop="$emit('close')">{{ $t('common.close') }}</button>
                        <button class="btn btn-sm btn-primary mx-1" :disabled="loading" @click.stop="generatePdf">{{ $t('common.print') }}</button>
                    </div>
                </div>
            </header>
            <main ref="mainContent" class="flex-grow-1 overflow-auto p-3" style="overflow-x: hidden !important;">
                <div class="print-page">
                    <div class="row">
                        <div class="col-6 nopads">
                            <h1 class="report-header">
                                {{ $t('trip.trip_with_observations_pdf_title')}}
                            </h1>
                            {{ getCurrentDay() }}
                        </div>
                        <div class="col-6 nopads" style="text-align: right">
                            <img src="../../assets/routa_dark.jpg"
                                 class="print-logo" alt="logo"/>
                        </div>
                    </div>
                    <hr />
                    <!-- Summary map -->
                    <div class="col-12">
                        <div class="map-container summary-map-container">
                            <map-container
                                ref="mapContainer"
                                :find-user="false"
                                @onMapChanged="mapChanged"
                            />
                        </div>
                    </div>
                    <!-- Trip summary data -->
                    <span class="span-title px-2 mt-3">{{ $t('work_report.trips') }}</span>
                    <vue3-datatable
                        v-if="tableTripItems.length > 0"
                        :rows="tableTripItems"
                        :columns="tripFields"
                        :pagination="false"
                        :sticky-header="true"
                        height="100%"
                        :sortable="true"
                        skin="bh-table-compact"
                        row-class="routa-table-row"
                        cell-class="routa-table-cell"
                        class="my-2"
                    >
                        <template #start_time="row">
                            <span>{{ toLocalTime(row.value.start_time) }}</span>
                        </template>
                        <template #end_time="row">
                            <span>{{ toLocalTime(row.value.end_time) }}</span>
                        </template>
                        <template #avg_speed="row">
                            <span>{{ getAverageSpeed(row.value.length, getDurationInMinutes(row.value.start_time, row.value.end_time)) }}</span>
                        </template>
                    </vue3-datatable>
                    <!-- Observation summary figures -->
                    <span class="span-title px-2 mt-3">{{ $t('menu.observations') }}</span>
                    <vue3-datatable
                        v-if="tableItems.length > 0"
                        :rows="tableItems"
                        :columns="fields"
                        :pagination="false"
                        :sticky-header="true"
                        height="100%"
                        :sortable="true"
                        skin="bh-table-compact"
                        row-class="routa-table-row"
                        cell-class="routa-table-cell"
                        class="my-2"
                    >
                    </vue3-datatable>
                    <!-- Weather data -->
                    <span class="span-title px-2 mt-3">{{ $t('weather_station.data_title') }}</span>
                    <table v-if="weatherItem" class="table table-sm mb-3 mx-2">
                        <thead>
                        <tr>
                            <th>{{ $t('contracts.external.station') }}</th>
                            <th>{{ weatherItem.station }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>{{ $t('contracts.external.time') }}</td>
                            <td>{{ weatherItem.time }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('contracts.external.air_temp') }}</td>
                            <td>{{ weatherItem.airTemp }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('contracts.external.road_temp') }}</td>
                            <td>{{ weatherItem.roadTemp }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('contracts.external.avg_wind') }}</td>
                            <td>{{ weatherItem.windMS }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('contracts.external.rain_type') }}</td>
                            <td>{{ weatherItem.rainType }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('contracts.external.rain_amount') }}</td>
                            <td>{{ weatherItem.rainSum }}</td>
                        </tr>
                        </tbody>
                    </table>
                    <div v-if="!weatherItem" class="col-12 p-2 mt-2">{{ loading > 0 ? $t('common.loading') : $t('trip_list.no_weather_data') }}</div>
                    <div class="col-12 px-2">
                        <span class="span-title">{{ $t('velho_equipment.additional_info') }}</span>
                        <textarea v-model="additionalInfo" class="form-control" rows="4"></textarea>
                    </div>
                </div>
                <observation-print-details
                    v-for="observation in observations"
                    ref="observations"
                    :key="observation.id"
                    :observation="observation"
                    :report-completed="reportCompleted"
                    :show-all-photos="showAllPhotos"
                    @remove="removeItem"
                />
            </main>
            <footer class="flex-shrink-0 p-3 border-top">
                <div class="d-flex justify-content-between">
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        @click.stop="scrollToTop"
                    >
                        ↑
                    </button>

                    <button
                        class="btn btn-sm btn-outline-secondary"
                        @click.stop="scrollToBottom"
                    >
                        ↓
                    </button>
                </div>
            </footer>
        </div>
        <div
            v-if="loading > 0"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import {vehicleHelper} from "@/components/mixins/VehicleMixin";
import MapContainer from "@/components/map/MapContainer";
import {geomTypes} from "@/components/mixins/ObservationMixin";
import {PdfGeneratorManager} from "@/modules/PdfGeneratorManager";
import {restApi} from "@/components/mixins/RestApiMixin";
import {observationHelper} from '../mixins/ObservationMixin'
import {mapHelper} from '../mixins/MapMixin'
import {imageCacheService} from "@/services/ImageCacheService";
import ObservationPrintDetails from "@/components/observation/ObservationPrintDetails";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import {pdfMixin} from "@/components/mixins/PdfMixin";
import {weatherHelper} from "../mixins/WeatherMixin.js";
import obsSvg from '@/assets/observation.svg'

export default {
    name: 'TripWithObservationsReport',
    components: {MapContainer, ObservationPrintDetails, Vue3Datatable},
    mixins: [timeUtils, restApi, observationHelper, mapHelper, vehicleHelper, pdfMixin, weatherHelper],
    props: {
        trip: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            icon: null,
            map: null,
            observations: [],
            reportCompleted: false,
            showAllPhotos: false,
            companyLogo: null,
            additionalInfo: '',
            loading: 0,
            fields: [
                {field: 'name', title: this.$t('road_reports.type')},
                {field: 'count', title: this.$t('road_reports.count')}
            ],
            tripFields: [
                { field: 'driver',  title: this.$t('trip_list.driver'), sort: false },
                { field: 'contract', title: this.$t('orders.contract'), sort: false },
                { field: 'order', title: this.$t('vehicle_position.order'), sort: false },
                { field: 'start_time', title: this.$t('trip_list.start_time'), sort: false },
                { field: 'end_time', title: this.$t('trip_list.end_time'), sort: false },
                { field: 'length', title: this.$t('trip_list.trip_length'), sort: false },
                { field: 'avg_speed', title: this.$t('trip_list.avg_speed'), sort: false}
            ],
            items: [],
            tableItems: [],
            tableTripItems: [],
            weatherItem: null,
        }
    },
    mounted() {
        this.tableTripItems = [this.trip]
        this.fetchWeatherData()
    },
    beforeUnmount() {
        imageCacheService.clear();
    },
    methods: {
        fetchWeatherData() {
            this.loading++
            const params = {
                time: this.trip.start_time,
                lat: this.trip.geometry[0].y,
                lon: this.trip.geometry[0].x
            }
            this.restFetchParams(this.weatherDataUrl, params, this.handleWeather)
        },

        handleWeather(response) {
            this.loading--
            if (response && response.data) {
                this.weatherItem = this.getWeatherDataItem(response.data)
            }
        },


        async generatePdf() {
            this.loading++

            const {
                pdfDoc, pdfService, fonts, colors, footerLogo, companyFooterLogo, pdfGeneratorManager
            } = await this.initializePdf()

            const screenshot = await this.getMapScreenshotAsArrayBuffer()
            let nextYPosition = await this.addPdfHeaderWithMap(this.$t('trip.trip_with_observations_pdf_title'), screenshot)

            // Trip data
            let headers = []
            this.tripFields.forEach(item => headers.push(item.title))
            const tripData = [
                headers,
                // Data
                ...this.tableTripItems.map(trip => {
                    return [
                        trip.driver,
                        trip.contract,
                        trip.order,
                        this.toLocalTime(trip.start_time),
                        this.toLocalTime(trip.end_time),
                        trip.length,
                        this.getAverageSpeed(trip.length, this.getDurationInMinutes(trip.start_time, trip.end_time))
                    ]
                })
            ]
            const tablePage = pdfDoc.getPage(pdfDoc.getPages().length -1)
            nextYPosition -= 15

            nextYPosition = pdfService.addText(pdfDoc, this.$t('work_report.trips'), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
            nextYPosition = pdfService.addTable(tripData, pdfDoc, {
                textFont: fonts.fontText,
                headerFont: fonts.fontBold,
                textFontSize: 8,
                headerFontSize: 9,
                textColor: colors.textColor,
                headerFontColor: colors.boldColor
            }, nextYPosition, undefined, undefined, footerLogo, undefined, undefined, undefined, companyFooterLogo)
            nextYPosition -= 25

            // Observation summary data
            nextYPosition = pdfService.addText(pdfDoc, this.$t('menu.observations'), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
            nextYPosition = pdfService.addTable([[this.$t('road_reports.type'), this.$t('road_reports.count')], ...this.items], pdfDoc, {
                textFont: fonts.fontText,
                headerFont: fonts.fontBold,
                textFontSize: 9,
                headerFontSize: 9,
                textColor: colors.textColor,
                headerFontColor: colors.boldColor
            }, nextYPosition, undefined, undefined, footerLogo, undefined, undefined, undefined, companyFooterLogo)

            // Weather data
            if (this.weatherItem) {
                nextYPosition -= 25
                const addNewPage = nextYPosition - 200 < pdfService.footerZone
                if (addNewPage) {
                    nextYPosition -= 500
                }

                nextYPosition = pdfService.addText(pdfDoc, this.$t('weather_station.data_title'), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
                nextYPosition = pdfService.addTable(this.getWeatherTable(this.weatherItem), pdfDoc, {
                    textFont: fonts.fontText,
                    headerFont: fonts.fontBold,
                    textFontSize: 9,
                    headerFontSize: 9,
                    textColor: colors.textColor,
                    headerFontColor: colors.boldColor
                }, nextYPosition, undefined, undefined, footerLogo, undefined, undefined, undefined, companyFooterLogo)

            } else {
                nextYPosition -= 25
                nextYPosition = pdfService.addText(pdfDoc, this.$t('weather_station.data_title'), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
                nextYPosition = pdfService.addText(pdfDoc, this.$t('trip_list.no_weather_data'), fonts.fontText, 10, nextYPosition, undefined, undefined, undefined, footerLogo)
            }

            nextYPosition -= 25

            // Additional info
            if(this.additionalInfo) {
                nextYPosition = pdfService.addText(pdfDoc, this.$t('velho_equipment.additional_info'), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
                this.additionalInfo.split('\n').forEach(line => {
                    if(line) {
                        nextYPosition = pdfService.addText(pdfDoc, line, fonts.fontText, 12, nextYPosition, undefined, undefined, 30, footerLogo, companyFooterLogo, colors.textColor)
                    } else {
                        nextYPosition -= 2
                    }
                })
            }

            const afterTablePage = pdfDoc.getPage(pdfDoc.getPages().length -1)

            if (afterTablePage === tablePage) {
                nextYPosition -= 500
            }

            nextYPosition -= 10

            const observations = JSON.parse(JSON.stringify(this.observations)).sort((a, b) => {
                return a.photos.length - b.photos.length
            })

            for (let i = 0; i < observations.length; i++) {
                const observation = this.observations[i]
                const photos = await this.fetchPhotos(observation, this.showAllPhotos)
                nextYPosition = await this.addObservationToPdf(observation, photos, nextYPosition, i)
            }

            const pdfBytes = await pdfGeneratorManager.generateBytesFromPdfDoc(pdfDoc)
            PdfGeneratorManager.downloadPdf(pdfBytes, this.$t('trip.trip_with_observations_pdf_title'))
            this.loading--
        },
        isPng(photo) {
            return photo.startsWith('data:image/png;');
        },
        async takePhotoOfMap(index) {
            const result = this.$refs.observations[index].getMarkerEditorMap()
            const ss = await result.takeScreenShot()
            return await new Response(ss).arrayBuffer();
        },
        async getMapScreenshotAsArrayBuffer() {
            const screenshot = await this.map.takeScreenShot()
            return await new Response(screenshot).arrayBuffer()
        },
        async fetchPhotos(observation, fetchAll = false) {
            const baseUrl = this.observationPhotoUrl;
            const id = observation.id;
            const results = [];

            // return early if there is no photos to handle
            if (!observation.photos || observation.photos.length === 0) {
                return results;
            }

            // if fetchAll is enabled return all photos else return the first photo like before
            const photosToFetch = fetchAll ? observation.photos : [observation.photos[0]];

            // Helper function to get a single photo from cache or fetch it from the API
            const getPhoto = async (photo) => {
                const photoId = photo.id.toString();

                if (imageCacheService.has(photoId)) {
                    return imageCacheService.get(photoId);
                } else {
                    const url = `${baseUrl}/${id}/${photo.id}`;
                    const response = await new Promise(resolve => this.restFetch(url, resolve));
                    return response.data;
                }
            };

            // loop through the photos and use the helper getPhoto to determine how to load it for pdf
            for (const photo of photosToFetch) {
                results.push(await getPhoto(photo));
            }

            return results;
        },

        mapChanged: function (map) {
            this.map = map
            this.icon = this.map.getMarkerStore().getSvgMarker(obsSvg, null, null, '2em', '2em')
            this.initView()
        },
        async initView() {
            if (this.trip && this.map) {
                //Remove any previous observations
                this.map.removeMapItemsByType(this.OBSERVATION)
                this.map.removeMapItemsByType(this.TRACE)

                let observationsData = []
                try {
                    observationsData = await new Promise((resolve, reject) => {
                        this.loading++
                        this.restFetchParams(this.observationUrl, {
                            from: this.trip.start_time,
                            to: this.trip.end_time,
                            reporter: this.trip.driver_id,
                            contract: this.trip.contract_id,
                        }, (response) => {
                            this.loading--
                            resolve(response.data)
                        }, (error) => {
                            this.loading--
                            reject(error)
                        })
                    })
                    this.observations = observationsData
                    // Draw observations
                    this.drawObservations()
                    // Draw the trip
                    this.drawTrip()
                    // Zoom to the trip
                    this.map.zoomToGroup(this.TRACE)
                    // eslint-disable-next-line no-empty
                } catch (error) {}
            }
            // Table summaries
            this.initItems()
        },

        updateObservationsToMap() {
            this.map.removeMapItemsByType(this.OBSERVATION)
            this.drawObservations()
        },

        initItems() {
            let table = []
            let pdf = []
            const ids = this.observations.map(ob => ob.type.id)

            const uniqueObservationsByType = this.observations.filter((observation, index, self) =>
                index === self.findIndex((o) => o.type.id === observation.type.id)
            )

            uniqueObservationsByType.forEach(obs => {
                pdf.push([
                        obs.type.name,
                        ids.filter(id => id === obs.type.id).length,
                    ]
                )
                table.push({
                        id: obs.type.id,
                        name: obs.type.name,
                        count: ids.filter(id => id === obs.type.id).length
                    }
                )

            })
            table.sort((a, b) => a.name.localeCompare(b.name));

            this.items = pdf
            this.tableItems = table
        },
        observationDetails(obs) {
            return [
                [this.$t('observations.id'), obs.id],
                [this.$t('observations.created_time'), this.toLocalTime(obs.created_time)],
                [this.$t('contracts.name'), obs.contract ? obs.contract.name : ''],
                [this.$t('contracts.customer'), obs.contract ? obs.contract.customer.name : ''],
                [this.$t('observations.order'), obs.work_order && obs.work_order.task_type ? obs.work_order.task_type.name : ''],
                [this.$t('observations.status'), obs.closed_time ? this.$t('observations.closed') : this.$t('observations.open')],
                [this.$t('observations.location'), this.getStreetAddressString(obs) + (obs.city ? ' ' + obs.city.name : '')],
            ]
        },
        drawTrip: function() {
            if (this.trip && this.trip.geometry) {
                let color = '#565659'
                let dash = false
                const arrow = false
                let width = 3
                color
                this.map.drawPolyLine(0, this.TRACE, this.trip.geometry, color, dash, arrow, width, 1, false, false, null, false)
            }
        },
        drawObservations: function () {
            for(let observation of this.observations) {
                if (observation.type.geometry_type === geomTypes.POINT) {
                    this.map.showMapMarker(observation.id, this.OBSERVATION, observation.geometry.point.y,
                        observation.geometry.point.x, this.icon, false, 0)
                } else if (observation.type.geometry_type === geomTypes.LINESTRING) {
                    const color = observation.closed_time ? '#bcbcbc' : '#bb330e';
                    const points = observation.geometry.line_string.points.map(point => {
                        return {x: point[0], y: point[1]}
                    });
                    this.map.drawPolyLine(observation.id, this.OBSERVATION, points, color, false, false, 3, 1, true)
                }
            }
        },

        removeItem(observationId) {
            const index = this.observations.findIndex(item => item.id === observationId)
            if (index >= 0) {
                this.observations.splice(index, 1)
                this.updateObservationsToMap()
                this.initItems()
            }
        },

        scrollToTop() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        },

        scrollToBottom() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }

    }
}
</script>

<style scoped>
.report-header {
    margin: 0;
    padding-top: .2em;
    font-size: 1.2em;
    font-weight: bold;
    text-transform: uppercase;
}

.summary-map-container {
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 1em;
    overflow: hidden;
    width: 750px;
    height: 350px;
    margin: auto;
    margin-bottom: .5em;
}

</style>

